import { useCallback, useEffect, useId, useRef } from 'react';
import clsx from 'clsx';
import Modal from '../_custom/Modal/Modal';
import Button from '../_custom/Button/Button';
import modalOverDrawerStyles from './modalOverDrawer.module.scss';
import styles from './ConfirmDialog.module.scss';

interface ConfirmDialogProps {
    open: boolean;
    title?: string;
    message?: string;
    /** Extra content between the message and the action row — e.g. a radio
     *  group of choices the confirm action applies (SheetEditor's
     *  delete/insert row-or-column dialog). */
    children?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Renders the confirm button in a destructive (red) style. */
    destructive?: boolean;
    onConfirm: () => void;
    /** Esc / backdrop dismiss. */
    onClose: () => void;
    /** The cancel button's own action, when it must differ from Esc/backdrop
     *  (`onClose`) — e.g. a dismiss that must not count as a discard.
     *  Defaults to `onClose`. */
    onCancel?: () => void;
    /** Focuses the confirm button on open, so Enter confirms. */
    autoFocusConfirm?: boolean;
    /** Keydown bubbling up from anything inside the dialog (including the
     *  focused buttons) — e.g. single-letter choice shortcuts. */
    onKeyDown?: (e: React.KeyboardEvent) => void;
}

/** Bounds the open-edge focus retry — `_custom/Modal` only flips its
 *  `display` to visible one render AFTER `open` becomes true. */
const FOCUS_RETRY_MAX_FRAMES = 10;

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title,
    message,
    children,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    onConfirm,
    onClose,
    onCancel = onClose,
    autoFocusConfirm = false,
    onKeyDown,
}) => {
    const confirmRef = useRef<HTMLButtonElement>(null);
    const titleId = useId();
    const messageId = useId();
    // Esc must behave exactly like the backdrop (onClose, never onCancel) —
    // Modal itself has no keyboard handling, so a host that relies only on
    // Modal's own backdrop click (e.g. the Reports table, outside
    // SheetEditor's own Esc-policy layer) would otherwise never dismiss on Esc.
    // Bubble phase, deliberately: SheetEditor's own capture-phase Esc policy
    // (escapeHandler.ts) calls stopImmediatePropagation for every outcome that
    // closes a dialog, so this listener never even runs there — it only fires
    // for a host with no capture-phase Esc policy of its own.
    const handleKeyDown = useCallback(
        (e: KeyboardEvent): void => {
            if (e.key === 'Escape') onClose();
        },
        [onClose],
    );

    useEffect(() => {
        if (!open) return undefined;
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, handleKeyDown]);

    useEffect(() => {
        if (!open || !autoFocusConfirm) return undefined;
        let attempts = 0;
        let frame = 0;
        const tryFocus = (): void => {
            attempts += 1;
            const el = confirmRef.current;
            if (el?.isConnected && el.offsetParent !== null) {
                el.focus();
                if (document.activeElement === el) return;
            }
            if (attempts < FOCUS_RETRY_MAX_FRAMES) frame = requestAnimationFrame(tryFocus);
        };
        frame = requestAnimationFrame(tryFocus);
        return () => cancelAnimationFrame(frame);
    }, [open, autoFocusConfirm]);

    return (
        <Modal open={open} onClose={onClose} size="flexible" className={modalOverDrawerStyles.modalOverDrawer} withoutCloseButton>
            <div
                className={styles.dialog}
                onKeyDown={onKeyDown}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : message ? messageId : undefined}
                aria-describedby={title && message ? messageId : undefined}
            >
                {title && (
                    <span id={titleId} className={styles.title}>
                        {title}
                    </span>
                )}
                {message && (
                    <span id={messageId} className={styles.message}>
                        {message}
                    </span>
                )}
                {children}
                <div className={modalOverDrawerStyles.actions}>
                    <Button text={cancelLabel} variant="purpleinverted" onClick={onCancel} className={modalOverDrawerStyles.actionBtn} />
                    <Button
                        text={confirmLabel}
                        variant="primary"
                        onClick={onConfirm}
                        buttonRef={confirmRef}
                        className={clsx(modalOverDrawerStyles.actionBtn, destructive && styles.destructiveBtn)}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default ConfirmDialog;
